import { defineStore } from 'pinia'
import userService from '@/services/user.service'

export const useUserStore = defineStore('user', {
  state: () => ({
    user: {
      id: null,
      email: '',
      firstName: '',
      lastName: '',
      middleName: '',
      position: '',
      isOwner: false,
      isStaff: false,
      isSuperuser: false,
      status: '',
      tariff: null,
    },
    currentStaffer: null,
    enterprises: [],
    selectedEnterprise: null,
    permissions: {
      journal_permissions: {},
      handbook_permissions: {},
    },
    loading: false,
    error: null,
  }),

  getters: {
    userFullName: (state) => {
      if (!state.user.firstName && !state.user.lastName) {
        return state.user.email || 'Пользователь'
      }
      const parts = [
        state.user.lastName,
        state.user.firstName,
        state.user.middleName,
      ].filter(Boolean)
      return parts.join(' ')
    },

    userPosition: (state) => {
      if (state.currentStaffer?.position?.name) {
        return state.currentStaffer.position.name
      }
      return 'Должность не указана'
    },

    currentEnterprise: (state) => {
      if (!state.selectedEnterprise) return null
      return state.enterprises.find(e => e.id === state.selectedEnterprise)
    },

    hasPermission: (state) => (type, action) => {
      if (state.user.isSuperuser) return true
      if (!state.permissions) return false
      
      const permissions = type === 'journal' 
        ? state.permissions.journal_permissions 
        : state.permissions.handbook_permissions
      
      return permissions?.[action] || false
    },
  },

  actions: {
    /**
     * Set user data from API response
     */
    setUser(apiData) {
      this.user = {
        id: apiData.id,
        email: apiData.email,
        firstName: apiData.first_name || '',
        lastName: apiData.last_name || '',
        middleName: apiData.middle_name || '',
        isOwner: apiData.is_owner || false,
        isStaff: apiData.is_staff || false,
        isSuperuser: apiData.is_superuser || false,
        status: apiData.status || '',
        tariff: apiData.tariff || null,
      }
    },

    /**
     * Set current staffer data
     */
    setCurrentStaffer(stafferData) {
      this.currentStaffer = {
        id: stafferData.id,
        firstName: stafferData.first_name || '',
        lastName: stafferData.last_name || '',
        middleName: stafferData.middle_name || '',
        abbr: stafferData.abbr || '',
        position: stafferData.position || null,
        status: stafferData.status || '',
        email: stafferData.email || '',
        isOwner: stafferData.is_owner || false,
        departments: stafferData.departments || [],
        isAllDepartments: stafferData.is_all_departments || false,
      }
    },

    /**
     * Set enterprises list
     */
    setEnterprises(enterprisesData) {
      this.enterprises = enterprisesData.map(e => ({
        id: e.id,
        name: e.name,
        information: e.information || '',
        tz: e.tz || 0,
        currentStaffer: e.current_staffer,
      }))
      
      // Auto-select first enterprise if not selected
      if (this.enterprises.length > 0 && !this.selectedEnterprise) {
        this.selectedEnterprise = this.enterprises[0].id
      }
    },

    /**
     * Set permissions
     */
    setPermissions(permissionsData) {
      this.permissions = {
        journal_permissions: permissionsData.journal_permissions || {},
        handbook_permissions: permissionsData.handbook_permissions || {},
      }
    },

    /**
     * Load user profile from API
     */
    async loadUserProfile() {
      this.loading = true
      this.error = null

      try {
        const response = await userService.getUserProfile()
        
        if (response.success && response.data) {
          this.setUser(response.data)
        } else {
          throw new Error(response.message || 'Ошибка загрузки профиля')
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Ошибка загрузки профиля'
        console.error('Error loading user profile:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Load enterprises from API
     */
    async loadEnterprises() {
      this.loading = true
      this.error = null

      try {
        const response = await userService.getEnterprises()
        
        if (response.success && response.data) {
          this.setEnterprises(response.data)
        } else {
          throw new Error(response.message || 'Ошибка загрузки предприятий')
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Ошибка загрузки предприятий'
        console.error('Error loading enterprises:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Select enterprise and load permissions
     */
    async selectEnterprise(enterpriseId) {
      this.loading = true
      this.error = null

      try {
        const response = await userService.setEnterprise(enterpriseId)
        
        if (response.success && response.data) {
          this.selectedEnterprise = enterpriseId
          this.setCurrentStaffer(response.data)
          this.setPermissions(response.data)
          
          // Save to localStorage for persistence
          localStorage.setItem('selected_enterprise', enterpriseId)
        } else {
          throw new Error(response.message || 'Ошибка выбора предприятия')
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Ошибка выбора предприятия'
        console.error('Error selecting enterprise:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    /**
     * Load saved enterprise from localStorage
     */
    loadSavedEnterprise() {
      const savedEnterpriseId = localStorage.getItem('selected_enterprise')
      if (savedEnterpriseId) {
        this.selectedEnterprise = parseInt(savedEnterpriseId)
      }
    },

    /**
     * Clear user data
     */
    clearUser() {
      this.user = {
        id: null,
        email: '',
        firstName: '',
        lastName: '',
        middleName: '',
        position: '',
        isOwner: false,
        isStaff: false,
        isSuperuser: false,
        status: '',
        tariff: null,
      }
      this.currentStaffer = null
      this.enterprises = []
      this.selectedEnterprise = null
      this.permissions = {
        journal_permissions: {},
        handbook_permissions: {},
      }
      this.error = null
      
      localStorage.removeItem('selected_enterprise')
    },

    /**
     * Initialize user data (call after login)
     */
    async initialize() {
      try {
        // Load user profile
        await this.loadUserProfile()
        
        // Load enterprises
        await this.loadEnterprises()
        
        // Load saved enterprise or select first
        this.loadSavedEnterprise()
        
        // If we have enterprises, set the selected one
        if (this.selectedEnterprise) {
          await this.selectEnterprise(this.selectedEnterprise)
        } else if (this.enterprises.length > 0) {
          await this.selectEnterprise(this.enterprises[0].id)
        }
      } catch (error) {
        console.error('Error initializing user data:', error)
        throw error
      }
    },
  },
})
